import React, { useState, useEffect } from 'react';
import yaml from 'js-yaml';

import { useDispatch, useSelector } from 'store/hooks';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';

import Typography from '@mui/material/Typography';
import SubmitButton from 'components/Submits/SubmitButton';
import { CodeTextField } from './CodeTextField';
import { StyScopesWrapper } from './CustomScopes';
import { ScopesProtectionsInfoModalButton } from './ScopesProtectionsInfoModalButton';

type CustomizeProtectionsProps = {
    selectedBind: string;
    missingScopes: string[];
};
const CustomizeProtections = ({
    selectedBind,
    missingScopes,
}: CustomizeProtectionsProps): React.ReactElement => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const { customProtectionsPerBind, defaultProtectionsPerBind } = useSelector(
        (vision) => vision.support,
    );
    const [customProtections, setCustomProtections] = useState<string | null>(
        null,
    );

    useEffect(() => {
        if (
            Object.prototype.hasOwnProperty.call(
                customProtectionsPerBind,
                'protections',
            )
        ) {
            setCustomProtections(yaml.safeDump(customProtectionsPerBind));
        }
    }, [customProtectionsPerBind]);

    const submitProtections = () => {
        if (!customProtections) return;
        dispatch.support.updateCustomProtections({
            bind: selectedBind,
            protections: yaml.safeLoad(customProtections),
        });
    };

    const handleProtections = (e: React.ChangeEvent<HTMLInputElement>) =>
        setCustomProtections(e.target.value);

    const missingScopesMessage = t('support.scopes.missingScopes', {
        missingScopes: missingScopes,
    });
    return (
        <StyScopesWrapper>
            <Typography variant="h5">
                {t('support.scopes.titleProtection')}
            </Typography>
            <ScopesProtectionsInfoModalButton
                buttonTitle={t('support.scopes.defaultProtections')}
                modalTitle={t('support.scopes.defaultProtections')}
                modalContent={yaml.safeDump(defaultProtectionsPerBind)}
            />
            <CodeTextField
                multiline
                value={customProtections}
                fullWidth
                onChange={handleProtections}
                aria-label="empty textarea"
                rows={20}
                variant="filled"
                placeholder={t('support.scopes.insertCustomProtections')}
            />
            <ScopesProtectionsInfoModalButton
                buttonTitle={t('support.scopes.currentProtections')}
                modalTitle={t('support.scopes.currentProtections')}
                modalContent={yaml.safeDump(customProtectionsPerBind)}
            />
            <SubmitButton
                sx={{ mr: '2rem' }}
                submit={submitProtections}
                disabled={!customProtections}
                buttonLabel={t('support.scopes.updateProtections')}
                dialogTitle={t('support.scopes.updateProtections')}
            >
                {parse(
                    t('support.scopes.confirmProtectionsUpdate', {
                        bind: selectedBind,
                    }),
                )}
            </SubmitButton>
            {missingScopes.length > 0 && (
                <Typography sx={{ color: (theme) => theme.palette.orange }}>
                    ⚠️ {missingScopesMessage}
                </Typography>
            )}
        </StyScopesWrapper>
    );
};

export default CustomizeProtections;
